// Outbox FE: cola en localStorage para los writes del framework de rollout (PUT switch
// + POST telemetría). Fire-and-forget: el handler del click NUNCA hace await sobre esto.
//
// Propiedades:
//  - Keyed por commerce_id → al cambiar de cuenta no se reenvían eventos de otra (purge/ignore).
//  - Dedupe por id (event_id ULID) → idempotente; encolar dos veces no duplica.
//  - Retry con backoff exponencial → sobrevive reload (persistido en localStorage).
//  - Drena en background; nunca propaga errores al caller.

import { getActiveCommerceId } from './cache';
import { postFeatureTelemetry, putFeatureVersion, TELEMETRY_BATCH_MAX } from '@services/featureVersionService';
import type { FeatureTelemetryEvent, FeatureVariant, OutboxItem } from './types';

const OUTBOX_PREFIX = 'plick:feature-outbox:v1:';
const MAX_ATTEMPTS = 6;
const BASE_BACKOFF_MS = 2000;

function outboxKey(commerceId: string): string {
  return `${OUTBOX_PREFIX}${commerceId}`;
}

function readQueue(commerceId: string): OutboxItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(outboxKey(commerceId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as OutboxItem[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(commerceId: string, items: OutboxItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    if (items.length === 0) window.localStorage.removeItem(outboxKey(commerceId));
    else window.localStorage.setItem(outboxKey(commerceId), JSON.stringify(items));
  } catch {
    // ignore quota / private-mode
  }
}

function backoffMs(attempts: number): number {
  return Math.min(BASE_BACKOFF_MS * 2 ** attempts, 5 * 60 * 1000);
}

// Guard de concurrencia keyed POR commerce_id (no un booleano global). Un drain en vuelo
// para el comercio A no debe bloquear el drain del comercio B: en multisucursal el cambio
// de sucursal puede ocurrir mientras un drain de A está esperando red, y un flag global
// dejaría sin drenar la cola de B hasta que A terminara. Cada comercio se serializa solo
// consigo mismo, coherente con el resto del outbox (todo keyed por commerce_id).
const draining = new Set<string>();

// Reset de PRODUCCIÓN: limpia el guard al cambiar de sesión/sucursal (login, logout,
// refreshLogin), coherente con resetFeatureVersionStore/Hydration. Si un drain quedó en vuelo
// (esperando red) al cambiar de sesión, su commerce_id sigue en el Set hasta que termine; un
// re-drain del MISMO comercio (logout→login, o switch A→B→A) se saltaría por `draining.has`.
// Vaciar aquí garantiza que la nueva sesión drene sin esperar al drain huérfano de la anterior.
export function resetOutboxDraining(): void {
  draining.clear();
}

// Drena la cola del commerce activo. Fire-and-forget: se llama sin await. Procesa los
// items vencidos (next_attempt_at <= now), agrupa la telemetría en un batch (<=50) y
// reintenta los switches uno a uno. Errores → backoff, no se pierde el item.
export async function drainOutbox(commerceId = getActiveCommerceId()): Promise<void> {
  if (!commerceId || draining.has(commerceId)) return;
  draining.add(commerceId);
  try {
    const now = Date.now();
    const queue = readQueue(commerceId);
    if (queue.length === 0) return;

    const due = queue.filter((it) => it.next_attempt_at <= now);
    if (due.length === 0) return;

    const succeeded = new Set<string>();
    const bumped = new Map<string, OutboxItem>();

    // 1) Telemetría → un solo POST batch.
    const telemetryItems = due.filter((it) => it.kind === 'telemetry' && it.event);
    if (telemetryItems.length > 0) {
      const events = telemetryItems.slice(0, TELEMETRY_BATCH_MAX).map((it) => it.event as FeatureTelemetryEvent);
      try {
        await postFeatureTelemetry(events);
        telemetryItems.slice(0, TELEMETRY_BATCH_MAX).forEach((it) => succeeded.add(it.id));
      } catch {
        telemetryItems.slice(0, TELEMETRY_BATCH_MAX).forEach((it) => {
          bumped.set(it.id, { ...it, attempts: it.attempts + 1, next_attempt_at: now + backoffMs(it.attempts + 1) });
        });
      }
    }

    // 2) Switches → uno a uno (idempotente en backend).
    const switchItems = due.filter((it) => it.kind === 'switch');
    for (const it of switchItems) {
      try {
        await putFeatureVersion({
          featureKey: it.feature_key,
          toVariant: it.to_variant as FeatureVariant,
          fromVariant: it.from_variant,
          trigger: it.trigger,
        });
        // ok:false (p.ej. REQUIRES_ROOT) NO es error de red: se considera procesado.
        succeeded.add(it.id);
      } catch {
        bumped.set(it.id, { ...it, attempts: it.attempts + 1, next_attempt_at: now + backoffMs(it.attempts + 1) });
      }
    }

    // 3) Reconstruir la cola: quitar éxitos + items agotados; aplicar backoff a los bumped.
    const next = queue
      .map((it) => bumped.get(it.id) ?? it)
      .filter((it) => !succeeded.has(it.id))
      .filter((it) => it.attempts < MAX_ATTEMPTS);

    writeQueue(commerceId, next);
  } finally {
    draining.delete(commerceId);
  }
}

// Encola un item (telemetría o switch) y dispara un drain en background. Dedupe por id.
function enqueue(item: OutboxItem): void {
  const commerceId = item.commerce_id;
  if (!commerceId) return;
  const queue = readQueue(commerceId);
  if (queue.some((it) => it.id === item.id)) return; // dedupe
  writeQueue(commerceId, [...queue, item]);
  drainOutbox(commerceId).catch(() => {
    // fire-and-forget: el drain nunca propaga; el item queda persistido para el próximo intento
  });
}

// Encola un evento de telemetría (fire-and-forget).
export function enqueueTelemetry(event: FeatureTelemetryEvent, commerceId = getActiveCommerceId()): void {
  if (!commerceId) return;
  const now = Date.now();
  enqueue({
    id: event.event_id,
    kind: 'telemetry',
    commerce_id: commerceId,
    feature_key: event.feature_key,
    event,
    attempts: 0,
    next_attempt_at: now,
    created_at: now,
  });
}

// Encola un switch de variante (PUT) fire-and-forget. `id` ULID para dedupe/idempotencia.
export function enqueueSwitch(
  args: { id: string; featureKey: string; toVariant: FeatureVariant; fromVariant?: FeatureVariant; trigger?: string },
  commerceId = getActiveCommerceId(),
): void {
  if (!commerceId) return;
  const now = Date.now();
  enqueue({
    id: args.id,
    kind: 'switch',
    commerce_id: commerceId,
    feature_key: args.featureKey,
    to_variant: args.toVariant,
    from_variant: args.fromVariant,
    trigger: args.trigger,
    attempts: 0,
    next_attempt_at: now,
    created_at: now,
  });
}
